import { calculatorRegistry, getCalculatorConfig } from "@/lib/constants/calculators";
import { countries, getSupportedCountries } from "@/lib/country/registry";

export interface CalculatorRedirectSuggestion {
  exists: boolean;
  calculatorName?: string;
  correctCountries?: Array<{ code: string; name: string; flag: string }>;
  requestedCountry?: string;
  requestedCountryName?: string;
  category?: string;
  slug?: string;
}

/**
 * Analyzes a 404 calculator request and determines if the calculator exists
 * but was requested with the wrong country code.
 */
export function analyzeCalculatorRequest(
  country: string,
  category: string,
  slug: string
): CalculatorRedirectSuggestion {
  const config = getCalculatorConfig(slug);
  const supportedCountries = getSupportedCountries();
  
  // Check if this country exists in our system
  const requestedCountryData = countries.find(c => c.code.toLowerCase() === country.toLowerCase());
  
  // If calculator doesn't exist at all, return not found
  if (!config) {
    return {
      exists: false,
      requestedCountry: country,
      requestedCountryName: requestedCountryData?.name,
    };
  }

  // If category doesn't match, return not found
  if (config.category !== category) {
    return {
      exists: false,
      calculatorName: config.name,
      requestedCountry: country,
      requestedCountryName: requestedCountryData?.name,
    };
  }

  // Check if calculator supports all countries
  if (config.countries === "all") {
    // Shouldn't be a 404 unless country code is invalid
    return {
      exists: false,
      calculatorName: config.name,
      requestedCountry: country,
      requestedCountryName: requestedCountryData?.name,
    };
  }

  // Calculator exists but is country-specific
  const correctCountryCodes = Array.isArray(config.countries) ? config.countries : [];
  const correctCountryData = correctCountryCodes
    .map(code => countries.find(c => c.code === code))
    .filter(Boolean) as Array<{ code: string; name: string; flag: string }>;

  return {
    exists: true,
    calculatorName: config.name,
    correctCountries: correctCountryData,
    requestedCountry: country,
    requestedCountryName: requestedCountryData?.name,
    category: config.category,
    slug: config.slug,
  };
}


